import React from 'react';
import DayColumn from './DayColumn';
import { Event } from './types'; // Event type is needed for DayColumn

type CalendarViewType = 'day' | 'week';

// Type for staff availability data (recurring working hours only)
interface StaffAvailabilityMap {
  recurring: { [dayOfWeek: number]: { start: string; end: string }[] };
}

interface CalendarViewProps {
  view: CalendarViewType;
  weekDays: Date[]; // Used in week view
  currentDate: Date; // Used in day view
  events: Event[];
  onOpenEventModal: (date: Date) => void; // Prop for DayColumn to add event
  onDeleteEvent?: (eventId: string | number) => Promise<void>; // Prop for DayColumn to delete event
  staffAvailability?: StaffAvailabilityMap; // Staff availability for showing "Not working" indicator
}

const CalendarView: React.FC<CalendarViewProps> = ({
  view,
  weekDays,
  currentDate,
  events,
  onOpenEventModal,
  onDeleteEvent,
  staffAvailability
}) => {
  // Must match DayColumn's HOUR_HEIGHT constant
  const HOUR_HEIGHT = 44;
  const HEADER_HEIGHT = 40; // h-10 in DayColumn

  return (
    <div className="overflow-x-auto overflow-y-auto h-full">
      <div className="min-w-[800px] h-full">
        {/* Week View */}
        {view === 'week' && (
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {/* Time column with hour markers */}
            <div className="w-12 bg-gray-50 relative" style={{ paddingTop: `${HEADER_HEIGHT}px` }}>
              {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
                <div key={hour} className="absolute text-xs text-gray-500 right-1" style={{ top: `${(hour - 8) * HOUR_HEIGHT + HEADER_HEIGHT}px` }}>
                  {hour > 12 ? `${hour - 12}PM` : hour === 12 ? '12PM' : `${hour}AM`}
                </div>
              ))}
            </div>

            <div className="flex-1 flex">
              {weekDays.map((date, index) => {
                const dayOfWeek = date.getDay();
                // Use recurring availability only (blocked times are shown as events)
                const dayAvailability = staffAvailability?.recurring[dayOfWeek] || [];
                return (
                  <DayColumn
                    key={index}
                    date={date}
                    events={events}
                    onAddEvent={onOpenEventModal}
                    onDeleteEvent={onDeleteEvent}
                    availableRanges={dayAvailability}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Day View */}
        {view === 'day' && (
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {/* Time column with hour markers */}
            <div className="w-12 bg-gray-50 relative" style={{ paddingTop: `${HEADER_HEIGHT}px` }}>
              {Array.from({ length: 12 }, (_, i) => i + 8).map((hour) => (
                <div key={hour} className="absolute text-xs text-gray-500 right-1" style={{ top: `${(hour - 8) * HOUR_HEIGHT + HEADER_HEIGHT}px` }}>
                  {hour > 12 ? `${hour - 12}PM` : hour === 12 ? '12PM' : `${hour}AM`}
                </div>
              ))}
            </div>

            <div className="flex-1">
              {(() => {
                const dayOfWeek = currentDate.getDay();
                // Use recurring availability only (blocked times are shown as events)
                const dayAvailability = staffAvailability?.recurring[dayOfWeek] || [];
                return (
                  <DayColumn
                    date={currentDate}
                    events={events}
                    onAddEvent={onOpenEventModal}
                    onDeleteEvent={onDeleteEvent}
                    availableRanges={dayAvailability}
                  />
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView; 